import React, { useEffect, useState } from "react";

import { faCircle } from "@fortawesome/free-regular-svg-icons";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { PhoneNumberUtil } from "google-libphonenumber";
import {
  Accordion,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { PhoneInput } from "react-international-phone";
import { useNavigate } from "react-router-dom";

import { DeliveryMethod } from "@/enums/App.enum";
import { getUrlLangPrefix } from "@/utils/Local";
import {
  formatDistance,
  formatMoney,
  getCheckoutReturnUrl,
  validateEmail,
} from "@/utils/Toolbox";

import CartItemInfo from "./CartItemInfo.component";
import StoreInfoModalComponent from "./StoreInfoModal.component";
import FaInfoCircle from "../assets/icons/faInfoCircle.svg";
import FaMapMarker from "../assets/icons/faMapMarker.svg";
import FaTruck from "../assets/icons/faTruck.svg";
import FaWarehouse from "../assets/icons/faWarehouse.svg";
import useCart from "../hooks/useCart";
import i18n from "../i18n";
import apiService from "../services/api";

import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import type { Layout } from "@stripe/stripe-js";

import type {
  ICart,
  ICartItem,
  IShippingInfo,
  ITaxDetails,
} from "@/interfaces/ICart";

const CheckoutFormComponent = (): React.JSX.Element => {
  const stripe = useStripe();
  const elements = useElements();
  const { cart, dispatch, REDUCER_ACTIONS } = useCart();
  const [isLoading, setIsLoading] = useState(false);

  const [contact, setContact] = useState(cart.contact);
  const { t } = i18n;
  const [shippingInfo, setShippingInfo] = useState<IShippingInfo>(
    {} as IShippingInfo,
  );
  const pickupAddress = "618 Av. Meloche, Dorval, QC H9P 2P4";
  const pickupGoogleMap = "https://maps.app.goo.gl/hLsSuZKwaGbW984R6";
  const [showStoreInformationModal, setShowStoreInformationModal] =
    useState(false);
  const handleStoreInfoModalClose = () => setShowStoreInformationModal(false);
  const navigate = useNavigate();

  const phoneUtil = PhoneNumberUtil.getInstance();

  const isPhoneValid = () => {
    try {
      return phoneUtil.isValidNumber(
        phoneUtil.parseAndKeepRawInput(contact.phone),
      );
    } catch (error) {
      return false;
    }
  };

  const handleUpdateCart = async (cartData: ICart) => {
    if (isLoading) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await apiService.checkout(cartData);
      dispatch({
        type: REDUCER_ACTIONS.UPDATE,
        updatedCart: response.data.cart,
      });
    } catch (error) {
      navigate(`${getUrlLangPrefix()}/${t("_route.error")}`);
    }
  };

  const updateShippingInfo = async (info: any) => {
    const cartData = { ...cart };
    cartData.delivery_method = DeliveryMethod.SHIPPING;
    cartData.shipping.info = info;
    await handleUpdateCart(cartData);
  };

  const deliveryMethods = [
    {
      id: DeliveryMethod.PICKUP,
      title: `${t("pick_up")} - ${t("free")}`,
      icon: <img alt={t("warehouse")} src={FaWarehouse} />,
    },
    {
      id: DeliveryMethod.SHIPPING,
      title: t("get_it_shipped"),
      icon: <img alt={t("truck")} src={FaTruck} />,
    },
  ];

  const isPayButtonDisabled = () =>
    isLoading ||
    !stripe ||
    !elements ||
    !isPhoneValid() ||
    !validateEmail(contact.email) ||
    (cart.delivery_method === DeliveryMethod.SHIPPING &&
      !cart.shipping.is_available);

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (isPayButtonDisabled()) {
      return;
    }

    if (!stripe || !elements) {
      // Stripe.js hasn't yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: getCheckoutReturnUrl(t),
      },
    });

    // This point will only be reached if there is an immediate error when
    // confirming the payment. Otherwise, your customer will be redirected to
    // your `return_url`. For some payment methods like iDEAL, your customer will
    // be redirected to an intermediate site first to authorize the payment, then
    // redirected to the `return_url`.
    if (
      error.message &&
      (error.type === "card_error" || error.type === "validation_error")
    ) {
      console.error(error.message);
    }

    setIsLoading(false);
  };

  const paymentElementOptions = {
    layout: "tabs" as Layout,
  };

  const handleAddressChange = async (event: any) => {
    setShippingInfo(event.value);

    if (
      event.complete &&
      (!shippingInfo.address ||
        shippingInfo.address.line1 !== event.value.address.line1 ||
        shippingInfo.address.state !== event.value.address.state)
    ) {
      await updateShippingInfo(event.value);
    }
  };

  const handleAddressFocusOut = async () => {
    // await delay(500)
    // await updateShippingInfo(shippingInfo)
  };

  const updateDeliveryMethod = async (method: string) => {
    if (method === cart.delivery_method) {
      return;
    }

    const cartData = { ...cart };
    cartData.delivery_method = method;
    await handleUpdateCart(cartData);
  };

  const isCompanyValid = () => contact.company.length <= 120;

  const handleUpdateContact = async () => {
    if (!validateEmail(contact.email)) {
      return;
    }

    if (contact.phone && !isPhoneValid()) {
      return;
    }

    if (contact.company && !isCompanyValid()) {
      return;
    }

    const cartData = { ...cart };
    cartData.contact = contact;

    setIsLoading(true);
    const response = await apiService.checkout(cartData);
    dispatch({ type: REDUCER_ACTIONS.UPDATE, updatedCart: response.data.cart });
  };

  useEffect(() => {
    setIsLoading(false);
  }, [cart]);

  return (
    <Container fluid className="checkout-container">
      <Container className="pt-4">
        <h2 className="mb-5">{t("checkout")}</h2>
        <form id="payment-form" onSubmit={handleSubmit}>
          <div className="d-flex flex-column flex-lg-row gap-3">
            <div
              className="flex-grow-1 order-2 order-lg-1"
              style={{ minWidth: "60%" }}
            >
              <Card>
                <Card.Body>
                  <Card.Title>{t("_checkout.contact_info_title")}</Card.Title>
                  <Row>
                    <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                      <Form.Label htmlFor="name">{t("name")}</Form.Label>
                      <Form.Control
                        required
                        id="name"
                        isInvalid={!contact.name || contact.name === ""}
                        onBlur={async () => handleUpdateContact()}
                        type="text"
                        value={contact.name}
                        onChange={(e) =>
                          setContact({ ...contact, name: e.target.value })
                        }
                      />
                      <Form.Text muted id="nameHelpBlock" />
                    </Col>

                    <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                      <Form.Label htmlFor="email">
                        {t("email_address")}
                      </Form.Label>
                      <Form.Control
                        required
                        id="email"
                        isInvalid={!validateEmail(contact.email)}
                        onBlur={async () => handleUpdateContact()}
                        type="email"
                        value={contact.email}
                        onChange={(e) =>
                          setContact({ ...contact, email: e.target.value })
                        }
                      />
                      <Form.Text muted id="emailHelpBlock">
                        {t("_checkout.email_help")}
                      </Form.Text>
                    </Col>

                    <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                      <Form.Label htmlFor="phone">
                        {t("phone_number")}
                      </Form.Label>

                      <PhoneInput
                        forceDialCode
                        required
                        defaultCountry="ca"
                        inputClassName={`form-control ps-5 ${contact.phone && !isPhoneValid() ? "is-invalid" : ""}`}
                        onBlur={async () => handleUpdateContact()}
                        onChange={(phone) => setContact({ ...contact, phone })}
                        placeholder=""
                        value={contact.phone}
                        countrySelectorStyleProps={{
                          buttonClassName: "m-0 p-0",
                          buttonStyle: {
                            height: "35px",
                            position: "absolute",
                            border: "none",
                            left: "2px",
                            top: "2px",
                          },
                          flagStyle: { height: "16px" },
                        }}
                        inputProps={{
                          name: "phone",
                          id: "phone",
                          autoComplete: "tel",
                        }}
                        inputStyle={{
                          height: "38px",
                          fontSize: "1rem",
                          borderRadius: "var(--bs-border-radius)",
                        }}
                      />

                      <Form.Text muted id="phoneHelpBlock" />
                    </Col>

                    <Col className="mb-4 mb-lg-0" lg={6} xs={12}>
                      <Form.Label htmlFor="company">
                        {t("company")} ({t("optional")})
                      </Form.Label>
                      <Form.Control
                        id="company"
                        isInvalid={!isCompanyValid()}
                        onBlur={async () => handleUpdateContact()}
                        type="text"
                        value={contact.company}
                        onChange={(e) =>
                          setContact({ ...contact, company: e.target.value })
                        }
                      />
                      <Form.Text muted id="companyHelpBlock" />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <Card.Title>
                    {t("_checkout.shipping_method_title")}
                  </Card.Title>
                  <Row>
                    {deliveryMethods.map((delivery_method) => (
                      <Col
                        key={delivery_method.id}
                        lg={6}
                        xs={12}
                        onClick={async () =>
                          updateDeliveryMethod(delivery_method.id)
                        }
                      >
                        <div
                          className={`d-flex flex-row align-items-center gap-2 rounded pakville-option-value-container ${cart.delivery_method === delivery_method.id ? "pakville-option-selected" : ""}`}
                        >
                          {delivery_method.icon}
                          {delivery_method.title}

                          {cart.delivery_method === delivery_method.id ? (
                            <FontAwesomeIcon
                              color="var(--info-color)"
                              icon={faCheckCircle}
                              style={{ marginLeft: "auto" }}
                            />
                          ) : (
                            <FontAwesomeIcon
                              color="#A1A1A1"
                              icon={faCircle as IconProp}
                              style={{ marginLeft: "auto" }}
                            />
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>

                  {cart.delivery_method === DeliveryMethod.SHIPPING &&
                  cart.shipping.info &&
                  cart.shipping.info.address &&
                  cart.shipping.info.address.line1 &&
                  !cart.shipping.is_available ? (
                    <Row>
                      <div
                        className="d-flex align-items-center justify-content-center text-muted text-danger"
                        style={{ fontSize: "15px" }}
                      >
                        <div className="d-flex align-items-start gap-2 text-danger">
                          <img
                            alt={t(`shipping_not_available_note`)}
                            height="1rem"
                            src={FaInfoCircle}
                            width="1rem"
                            style={{
                              filter:
                                "brightness(0) saturate(100%) invert(20%) sepia(83%) saturate(5401%) hue-rotate(357deg) brightness(95%) contrast(129%)",
                            }}
                          />
                          {t("_checkout.shipping_not_available_note")}
                        </div>
                      </div>
                    </Row>
                  ) : (
                    <Row>
                      {cart.delivery_method === DeliveryMethod.SHIPPING &&
                      cart.shipping.is_available &&
                      cart.shipping.cost === 0 ? (
                        <div
                          className="d-flex align-items-center justify-content-center text-muted"
                          style={{ fontSize: "15px" }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <img
                              alt={t(`free_shipping_note`)}
                              className="mt-1"
                              height="1rem"
                              src={FaInfoCircle}
                              width="1rem"
                            />
                            {t("_checkout.free_shipping_note")}
                          </div>
                        </div>
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center text-muted"
                          style={{ fontSize: "15px" }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            {cart.delivery_method === DeliveryMethod.PICKUP ? (
                              <React.Fragment>
                                <img
                                  alt={t(`pickup`)}
                                  className="mt-1"
                                  height="1rem"
                                  src={FaMapMarker}
                                  width="1rem"
                                />
                                <a
                                  href={pickupGoogleMap}
                                  rel="noreferrer"
                                  target="_blank"
                                >
                                  {pickupAddress}
                                </a>
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                <img
                                  alt={t(`shipping_availability_note`)}
                                  className="mt-1"
                                  height="1rem"
                                  src={FaInfoCircle}
                                  width="1rem"
                                />
                                {t("_checkout.shipping_availability_note", {
                                  x: 100,
                                })}
                              </React.Fragment>
                            )}
                          </div>
                          <Button
                            className="m-0 p-0 ms-auto text-end"
                            onClick={() => setShowStoreInformationModal(true)}
                            variant="link"
                          >
                            {t("view_store_information")}
                          </Button>
                        </div>
                      )}
                    </Row>
                  )}

                  <Row>
                    <Col>
                      {cart.delivery_method === DeliveryMethod.SHIPPING && (
                        <AddressElement
                          className="mt-3"
                          onBlur={async () => handleAddressFocusOut()}
                          onChange={async (event) => handleAddressChange(event)}
                          options={{
                            mode: "shipping",
                            allowedCountries: ["CA"],
                            fields: {
                              phone: "always",
                            },
                            defaultValues:
                              cart.shipping.info && cart.shipping.info.address
                                ? cart.shipping.info
                                : {
                                    address: {
                                      state: "QC",
                                      country: "CA",
                                    },
                                  },
                          }}
                        />
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Body>
                  <Card.Title>
                    {t("_checkout.payment_options_title")}
                  </Card.Title>
                  <Row>
                    <Col xs={12}>
                      <PaymentElement
                        id="payment-element"
                        options={paymentElementOptions}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <div className="d-block d-lg-none d-grid mt-5 mb-5">
                <Button
                  disabled={isPayButtonDisabled()}
                  type="submit"
                  variant="primary d-flex justify-content-center"
                >
                  {isLoading ? (
                    <React.Fragment>
                      <Spinner animation="border" className="p-2" size="sm" />
                      <span style={{ opacity: 0 }}>.</span>
                    </React.Fragment>
                  ) : (
                    t("pay_now")
                  )}
                </Button>
              </div>
            </div>

            <div className="order-1 order-lg-2" style={{ minWidth: "40%" }}>
              <Card>
                <Card.Body>
                  <Card.Title className="mb-4">{t("order_summary")}</Card.Title>

                  <div className="d-flex justify-content-between mb-3">
                    <span>{t("sub_total")}</span>
                    <span>{formatMoney(cart.subtotal / 100)}</span>
                  </div>

                  {cart.discount > 0 && (
                    <div className="d-flex justify-content-between mb-3">
                      <span>{t("discount")}</span>
                      <span className="text-danger">
                        -{formatMoney(cart.discount / 100)}
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between mb-3">
                    {isLoading ? (
                      <span />
                    ) : (
                      <React.Fragment>
                        {cart.delivery_method === DeliveryMethod.SHIPPING ? (
                          <span>
                            {t("Shipping")}
                            {cart.shipping.distance > 0 && (
                              <span>
                                {" "}
                                ({formatDistance(cart.shipping.distance)})
                              </span>
                            )}
                          </span>
                        ) : (
                          <span>{t("pickup")}</span>
                        )}
                      </React.Fragment>
                    )}
                    {isLoading ? (
                      <span>{t("calculating")}</span>
                    ) : cart.delivery_method === DeliveryMethod.SHIPPING &&
                      !cart.shipping.is_available ? (
                      <span>{formatMoney(cart.shipping.cost / 100)}</span>
                    ) : (
                      <span>
                        {cart.shipping.cost > 0
                          ? formatMoney(cart.shipping.cost / 100)
                          : t("free")}
                      </span>
                    )}
                  </div>

                  {!isLoading && cart.taxes && cart.taxes.details
                    ? cart.taxes.details.map((tax: ITaxDetails) => (
                        <div
                          key={tax.label}
                          className="d-flex justify-content-between mb-3"
                        >
                          <span>
                            Tax {tax.label} ({tax.percentage}%)
                          </span>
                          <span>{formatMoney(tax.value / 100)}</span>
                        </div>
                      ))
                    : null}

                  <hr style={{ margin: "1rem 0" }} />

                  <div className="d-flex justify-content-between mb-3 fw-bold">
                    <span>{t("total")}</span>
                    {isLoading ? (
                      <React.Fragment>{t("calculating")}</React.Fragment>
                    ) : (
                      <span>{formatMoney(cart.calculated_total / 100)}</span>
                    )}
                  </div>

                  <div className="d-grid mt-5 mb-5">
                    <Button
                      disabled={isPayButtonDisabled()}
                      type="submit"
                      variant="primary d-flex justify-content-center"
                    >
                      {isLoading ? (
                        <React.Fragment>
                          <Spinner
                            animation="border"
                            className="p-2"
                            size="sm"
                          />
                          <span style={{ opacity: 0 }}>.</span>
                        </React.Fragment>
                      ) : (
                        t("pay_now")
                      )}
                    </Button>
                  </div>

                  <Accordion flush defaultActiveKey="0">
                    <Accordion.Item className="border-0 " eventKey="0">
                      <Accordion.Header> {t("items")}</Accordion.Header>
                      <Accordion.Body>
                        {cart.items.map((item: ICartItem, index: number) => (
                          <div key={index}>
                            <CartItemInfo item={item} />
                            <hr style={{ margin: "1rem 0" }} />
                          </div>
                        ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </Card.Body>
              </Card>
            </div>
          </div>
        </form>
      </Container>

      <StoreInfoModalComponent
        onClose={handleStoreInfoModalClose}
        show={showStoreInformationModal}
      />
    </Container>
  );
};

export default CheckoutFormComponent;
